import { v4 as uuid } from "uuid";
import { Dispatch, useContext, useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { Wallet } from "@src/core/models/wallet";
import { BloctoWalletContext } from "@src/core/contexts/blocto-wallet";
import { MetaMaskWalletContext } from "@src/core/contexts/meta-mask-wallet";
import { Offer } from "@src/core/models/offer";
import { Asset } from "@src/core/models/asset";
import { HttpClient } from "@src/core/api/http-client";
import endpoints from "@src/core/api/endpoints";
import { Order, OrderStatus } from "@src/core/models/order";
import TitleSubSection from "@src/presentation/components/shared/title/sub-section";
import { WALLET_OPEN_MODAL_BUTTON_ID } from "@src/presentation/components/wallet/open-modal-button";

interface Props {
  assets: Asset[];
  offer: Offer;
}

const OfferBuyNow = ({ assets, offer }: Props): JSX.Element => {
  const history = useHistory();
  const [selectedAsset, setSelectedAsset] = useState<Asset>();
  const isUniqueEdition = assets.length === 1;
  const { bloctoWallet } = useContext(BloctoWalletContext);
  const { metaMaskWallet } = useContext(MetaMaskWalletContext);
  const currentWallet = bloctoWallet || metaMaskWallet;

  useEffect(() => {
    if (isUniqueEdition) {
      setSelectedAsset(assets[0]);
    }
  }, [assets, isUniqueEdition]);

  const renderPurchaseButton = (): JSX.Element => {
    return (
      <Button type="submit" disabled={!selectedAsset}>
        <span>Proceed to checkout</span>
      </Button>
    );
  };

  const renderConnectWalletButton = (): JSX.Element => {
    return (
      <Button type="button" onClick={() => triggerWalletConnectModal()}>
        <span>Connect wallet to buy now</span>
      </Button>
    );
  };

  const handleSubmit = async (event: any): Promise<void> => {
    event.preventDefault();

    if (!selectedAsset) return undefined;

    if (!currentWallet) {
      return triggerWalletConnectModal();
    }

    const redirectToCheckout = (orderId: string) => {
      history.push(`/checkout-new/${orderId}`);
    };

    return submitOrder(offer, selectedAsset, currentWallet, redirectToCheckout);
  };

  return (
    <Card>
      <Card.Body>
        <TitleSubSection text="Buy now" />
        <Form onSubmit={handleSubmit}>
          <Row className="align-items-center">
            <Col xs={12}>
              <Form.Group>
                {assets &&
                  !isUniqueEdition &&
                  renderMultipleEditionsAction(
                    assets,
                    selectedAsset,
                    setSelectedAsset
                  )}
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                {!currentWallet && renderConnectWalletButton()}
                {currentWallet && renderPurchaseButton()}
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );
};

const renderMultipleEditionsAction = (
  assets: Asset[],
  selectedAsset: Asset | undefined,
  setSelectedAsset: Dispatch<Asset | undefined>
): JSX.Element => {
  const availableAssets = assets
    .filter((asset) => !!JSON.parse(asset.available.toString()))
    .sort((asset, previousAsset) =>
      Number(asset.editionId) > Number(previousAsset.editionId) ? 1 : -1
    );
  const totalEditionsCount = assets.length;
  const remainingEditionsCount = availableAssets.length;
  const formattedPlaceholder = `Select the edition (${remainingEditionsCount} left)`;

  return (
    <>
      <Form.Select
        value={selectedAsset?.id}
        onChange={(event) => {
          const newSelectedAsset = assets.find(
            (asset) => asset.id === event.target.value
          );

          return setSelectedAsset(newSelectedAsset);
        }}
      >
        <option>{formattedPlaceholder}</option>
        {availableAssets.map((asset) => {
          return (
            <option key={uuid()} value={asset.id}>
              {`${asset.editionId}/${totalEditionsCount} edition`}
            </option>
          );
        })}
      </Form.Select>
    </>
  );
};

const submitOrder = async (
  offer: Offer,
  selectedAsset: Asset,
  wallet: Wallet,
  onSuccess: (orderId: string) => void
): Promise<void> => {
  const payload = {
    assetId: selectedAsset.id,
    offerId: offer.id,
    wallet: wallet.id,
    price: offer.price,
    status: OrderStatus.AwatingConfirmation,
  };

  HttpClient.post(endpoints.ordersCreate(), payload, (order: Order) =>
    onSuccess(order.id)
  );
};

const triggerWalletConnectModal = () => {
  const $openModalButton = document.getElementById(WALLET_OPEN_MODAL_BUTTON_ID);

  $openModalButton?.click();
};

export default OfferBuyNow;
